import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, store } from '../store';
import { resetConnection, setReconnecting } from '../store/connectionSlice';
import { clearConversation, clearSpeechRateAlert, addTranscript, updateSpeechRateAnalysis, markSessionAsSaved, setIsSalespersonHost, addQuestion, addAnswer, addSpeechRateAlert, updateEntryFeedback } from '../store/conversationSlice';
import { logoutUser } from '../store/authSlice';
import { websocketService } from '../services/websocketService';
import { cleanupService } from '../services/cleanupService';
import { getAuth, signOut } from 'firebase/auth';
import TopBar from '../components/TopBar';
import ConversationFeed from '../components/ConversationFeed';
import { CombinedInfoBar } from '../components/CombinedInfoBar';
import AudioCaptureModal from '../components/AudioCaptureModal';
import { MeetBotModal } from '../components/MeetBotModal';
import { ResumeSessionModal } from '../components/ResumeSessionModal';
import { ManualCaptureModal } from '../components/ManualCaptureModal';
import { TranscriptionResult } from '../services/audioCaptureService';
import { speechRateService } from '../services/speechRateService';
import { testSpeechRateService } from '../services/speechRateService.test';
import { conversationService } from '../services/conversationService';
import { LandmineAlertsContainer } from '../components/LandmineAlert';
import CompetitorAlert from '../components/CompetitorAlert';
import { landmineWebSocketService } from '../services/landmineWebSocketService';
import { competitorWebSocketService } from '../services/competitorWebSocketService';
import { removeLandmineAlert, clearLandmineAlerts, addLandmineAlert } from '../store/slices/landmineSlice';
import { removeCompetitorAlert, clearCompetitorAlerts, addCompetitorAlert } from '../store/slices/competitorSlice';
import { calendarWebSocketService, CalendarBot } from '../services/calendarWebSocketService';
import { BotSelector } from '../components/BotSelector';
import { sessionManager } from '../services/sessionManager';
import { generateClientId } from '../utils/clientId';
import { config } from '../config';
import { useStandaloneSession } from '../hooks/useStandaloneSession';
import { TranscriptData } from '../services/standaloneWebSocketService';
import { useSessionSummary } from '../hooks/useSessionSummary';
import SessionSummaryModal from '../components/SessionSummaryModal';
import SalesPlaybookPanel from '../components/SalesPlaybookPanel';
import { restoreSalesPlaybookState, fetchRoleData, fetchIndustries } from '../store/slices/salesPlaybookSlice';

const ConversationPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Check if standalone mode is enabled via URL parameter
  // Only connect to standalone lobby when ?standalone=true is present
  const isStandaloneModeEnabled = useMemo(() => {
    return searchParams.get('standalone') === 'true';
  }, [searchParams]);
  const { isConnected, isReconnecting } = useSelector((state: RootState) => state.connection);
  const { lastSpeechRateAlert, transcripts, speechRateSettings, entries, sessionSaved } = useSelector((state: RootState) => state.conversation);
  const { isAuthenticated, user, tokenInfo, isMongoVerified } = useSelector((state: RootState) => state.auth);
  const [showSpeechAlert, setShowSpeechAlert] = useState(false);
  // const [recentTranscriptions, setRecentTranscriptions] = useState<TranscriptionResult[]>([]);
  const [isAudioCaptureActive, setIsAudioCaptureActive] = useState(false);
  const [salesPlaybookPanelHeight, setSalesPlaybookPanelHeight] = useState(600);
  const [maxPanelHeight, setMaxPanelHeight] = useState(600);

  // Refs for dynamic max-height calculation
  const leftColRef = useRef<HTMLDivElement>(null);
  const audioSettingsRef = useRef<HTMLDivElement>(null);
  const botSelectorRef = useRef<HTMLDivElement>(null);
  // Tracks whether the panel has been pinned to its initial max on first measurement
  const isPanelInitialized = useRef(false);

  // Ref + state for the fixed header (navbar + speech alert + info bar)
  // Used to set the main content marginTop dynamically so nothing is hidden behind the header
  const fixedHeaderRef = useRef<HTMLDivElement>(null);
  const [fixedHeaderHeight, setFixedHeaderHeight] = useState(92);
  // isSavingSession state removed - session saving now handled by saver_service
  const [speechRateAlerts, setSpeechRateAlerts] = useState<any[]>([]);
  const [showMeetBotModal, setShowMeetBotModal] = useState(false);
  const [isBotConnected, setIsBotConnected] = useState(false);
  const [clientId, setClientId] = useState<string>('');

  // Calendar integration state
  const [calendarBots, setCalendarBots] = useState<CalendarBot[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string | null>(null);
  const [connectingBotId, setConnectingBotId] = useState<string | null>(null); // Loading state for bot connection
  const [isCalendarConnected, setIsCalendarConnected] = useState(false);

  // Track bot type and session info for removal
  const [currentBotType, setCurrentBotType] = useState<'manual' | 'calendar' | null>(null);
  const [currentBotSessionId, setCurrentBotSessionId] = useState<string | null>(null);
  const [currentBotDisplayId, setCurrentBotDisplayId] = useState<string | null>(null); // For displaying in modal

  // Session resume state
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumableBots, setResumableBots] = useState<CalendarBot[]>([]);

  // Manual audio capture state
  const [showManualCaptureModal, setShowManualCaptureModal] = useState(false);

  // Calendar settings (fetched on mount for bot name)
  const [calendarSettings, setCalendarSettings] = useState<{ botName?: string } | null>(null);

  // Session summary hook for showing summary when bot is stopped
  const {
    showSessionSummary,
    sessionSummaryData,
    computeAndShowSummary,
    closeSummary,
    clearSessionData,
  } = useSessionSummary();

  // Standalone session handling - listens for desktop app sessions
  const handleStandaloneTranscript = React.useCallback((data: TranscriptData) => {
    console.log('[ConversationPage] Standalone transcript:', data.speaker, data.text.substring(0, 50));
    // Add transcript to Redux store (same format as bot transcripts)
    dispatch(addTranscript({
      text: data.text,
      speaker: data.speaker,
      timestamp: data.timestamp
    }));
  }, [dispatch]);

  const handleStandaloneAlert = React.useCallback((type: string, data: any) => {
    console.log('[ConversationPage] Standalone alert:', type, data);
    // Handle alerts from standalone session (same format as bot alerts)
    // The data comes from backend AI services via standalone_session_service
    switch (type) {
      case 'question_detected':
        // Backend sends complete question data
        if (data.question && data.category && typeof data.relevance_score === 'number') {
          dispatch(addQuestion({
            question: data.question,
            category: data.category,
            relevance_score: data.relevance_score,
            timestamp: data.timestamp || new Date().toISOString()
          }));
        }
        break;
      case 'landmine_alert':
        // Backend sends complete landmine alert data
        if (data.trigger && data.matched_text) {
          dispatch(addLandmineAlert(data));
        }
        break;
      case 'competitor_alert':
        // Backend sends complete competitor alert data
        if (data.competitor && data.matched_text) {
          dispatch(addCompetitorAlert(data));
        }
        break;
      case 'speech_rate_alert':
        // Backend sends complete speech rate alert data
        if (typeof data.total_words === 'number' && typeof data.max_words === 'number') {
          dispatch(addSpeechRateAlert({
            total_words: data.total_words,
            max_words: data.max_words,
            time_window: data.time_window || 30,
            words_per_minute: data.words_per_minute || 0,
            timestamp: data.timestamp || new Date().toISOString()
          }));
        }
        break;
    }
  }, [dispatch]);

  const {
    isLobbyConnected: isStandaloneLobbyConnected,
    isSessionConnected: isStandaloneSessionConnected,
    activeSession: standaloneSession,
    error: standaloneError,
    reconnectLobby: reconnectStandaloneLobby
  } = useStandaloneSession({
    // Only enable standalone connection when:
    // 1. URL has ?standalone=true parameter (opened for desktop app use)
    // 2. User is authenticated
    // 3. NOT in bot mode (mutual exclusion)
    enabled: isStandaloneModeEnabled && isAuthenticated && !!tokenInfo?.token && !isBotConnected,
    token: tokenInfo?.token || null,
    onTranscript: handleStandaloneTranscript,
    onAlert: handleStandaloneAlert
  });

  // Connect backend services (landmine, competitor) when standalone session starts
  // This enables direct alert routing from backend services to frontend (same as bot mode)
  useEffect(() => {
    if (standaloneSession?.roomId) {
      const roomId = standaloneSession.roomId;
      console.log(`[ConversationPage] Standalone session active - connecting backend services with room_id: ${roomId}`);

      // Update sessionManager with the room_id
      sessionManager.setRoomId(roomId);

      // Connect to backend services with the room_id
      // Landmine service
      landmineWebSocketService.connect().then(() => {
        landmineWebSocketService.joinRoom(roomId);
        console.log(`[ConversationPage] Landmine service joined room: ${roomId}`);
      }).catch((err) => {
        console.warn('[ConversationPage] Failed to connect landmine service:', err);
      });

      // Competitor service
      competitorWebSocketService.connect().then(() => {
        competitorWebSocketService.joinRoom(roomId);
        console.log(`[ConversationPage] Competitor service joined room: ${roomId}`);
      }).catch((err) => {
        console.warn('[ConversationPage] Failed to connect competitor service:', err);
      });

      // Reconnect production_final websocket service with the new room_id
      // This is critical for receiving objection/FAQ alerts from production_final_server
      websocketService.reconnectWithNewRoomId().then(() => {
        console.log(`[ConversationPage] Production server reconnected with room_id: ${roomId}`);
      }).catch((err) => {
        console.warn('[ConversationPage] Failed to reconnect production server:', err);
      });

      // Set isSalespersonHost to true for standalone mode (user is always the salesperson)
      dispatch(setIsSalespersonHost(true));

      return () => {
        // Cleanup when session ends or component unmounts
        console.log('[ConversationPage] Standalone session cleanup - disconnecting backend services');
        landmineWebSocketService.disconnect();
        competitorWebSocketService.disconnect();
      };
    }
  }, [standaloneSession?.roomId, dispatch]);

  // Get alerts from Redux store
  const landmineAlerts = useSelector((state: RootState) => state.landmine?.alerts || []);
  const competitorAlerts = useSelector((state: RootState) => state.competitor?.alerts || []);
  const landmineAllAlerts = useSelector((state: RootState) => state.landmine?.allAlerts || []);
  const competitorAllAlerts = useSelector((state: RootState) => state.competitor?.allAlerts || []);
  const isLandmineConnected = useSelector((state: RootState) => state.landmine?.isConnected || false);
  const isCompetitorConnected = useSelector((state: RootState) => state.competitor?.isConnected || false);

  // Initialize bot connected state - clear localStorage on mount since bot connection
  // should only be set when actively connecting via MeetBotModal
  // Also generate unique clientId for this session
  useEffect(() => {
    // Generate unique client ID for this session
    const newClientId = generateClientId();
    setClientId(newClientId);
    console.log('🆔 Generated client ID:');

    // Clear any stale bot info from localStorage on component mount
    // This ensures a fresh start and prevents showing "Remove Bot" when no bot is connected
    localStorage.removeItem('salesguru_bot_id');
    localStorage.removeItem('salesguru_session_id');
    localStorage.removeItem('salesguru_is_salesperson_host');
    setIsBotConnected(false);
  }, []);

  // Fetch calendar settings on mount (non-blocking) for bot name default
  useEffect(() => {
    const fetchCalendarSettings = async () => {
      try {
        const token = tokenInfo?.token;
        if (!token) return;

        const backendUrl = config.server.backendUrl || 'http://localhost:5000';
        const response = await fetch(`${backendUrl}/api/calendar/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) {
            setCalendarSettings(data.settings);
          }
        }
      } catch (error) {
        // Non-blocking - silently fail, will use default bot name
        console.debug('[ConversationPage] Could not fetch calendar settings:', error);
      }
    };

    fetchCalendarSettings();
  }, [tokenInfo?.token]);

  // Auto-clear bot state when no active bots remain or selected bot is terminated
  // This ensures "Remove Bot" button updates when bots are terminated externally (e.g., removed from Google Meet)
  useEffect(() => {
    if (!isBotConnected || !currentBotDisplayId) {
      return; // Nothing to check
    }

    // Check if the currently selected bot still exists and is active
    const selectedBot = calendarBots.find(bot =>
      bot.botId === currentBotDisplayId
    );

    // If selected bot is terminated or doesn't exist, clear state
    if (!selectedBot || selectedBot.status === 'terminated' || selectedBot.status === 'failed') {
      console.log('📅 Selected bot is no longer active, clearing bot connected state');
      setIsBotConnected(false);
      setCurrentBotType(null);
      setCurrentBotSessionId(null);
      setCurrentBotDisplayId(null);
      setSelectedBotId(null);
    }
  }, [calendarBots, isBotConnected, currentBotDisplayId, currentBotSessionId]);

  // OLD disabled useEffect - kept for reference
  // useEffect(() => {
  //   // Check if there are any active/joining/creating bots
  //   const hasActiveBots = calendarBots.some(bot =>
  //     bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
  //   );

  //   // If we have active bots but isBotConnected is false, update it
  //   if (hasActiveBots && !isBotConnected) {
  //     console.log('📅 Found active calendar bots, setting isBotConnected = true');
  //     setIsBotConnected(true);

  //     // Also set the current bot info if we have a selected bot
  //     const activeBot = calendarBots.find(b => b.botId === selectedBotId);
  //     if (activeBot) {
  //       setCurrentBotType('calendar');
  //       setCurrentBotSessionId(activeBot.botSessionId);
  //       setCurrentBotDisplayId(activeBot.botId);
  //     }
  //   }

  //   // If we don't have active bots but isBotConnected is true (and currentBotType is calendar), update it
  //   if (!hasActiveBots && isBotConnected && currentBotType === 'calendar') {
  //     console.log('📅 No active calendar bots found, setting isBotConnected = false');
  //     setIsBotConnected(false);
  //     setCurrentBotType(null);
  //     setCurrentBotSessionId(null);
  //     setCurrentBotDisplayId(null);
  //     setSelectedBotId(null);
  //   }
  // }, [calendarBots, isBotConnected, currentBotType, selectedBotId]);

  // Alert dismiss functions
  const dismissLandmineAlert = (index: number) => {
    dispatch(removeLandmineAlert(index));
  };

  const dismissCompetitorAlert = (index: number) => {
    dispatch(removeCompetitorAlert(index));
  };

  // Services are now connected through websocketService
  // No need for separate connection logic here

  // Check authentication status first
  useEffect(() => {
    if (!isAuthenticated || !tokenInfo?.token) {
      console.log('🔐 User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    if (!isMongoVerified) {
      console.log('🔍 User not verified in MongoDB, redirecting to login page');
      navigate('/login');
      return;
    }
  }, [isAuthenticated, tokenInfo, isMongoVerified, navigate]);

  // Connect to calendar service on mount
  // Note: No cleanup function - the service handles reconnection internally
  // and has duplicate connection prevention. Disconnecting in cleanup causes
  // issues with React StrictMode which unmounts/remounts components.
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      return;
    }

    console.log('📅 Connecting to calendar service on mount with Firebase UID:', user.uid);

    calendarWebSocketService.connect(undefined, user.uid)
      .then(() => {
        console.log('✅ Calendar service connected successfully');
        setIsCalendarConnected(true);
      })
      .catch((error) => {
        console.warn('⚠️ Could not connect to calendar service:', error);
        setIsCalendarConnected(false);
      });

    // No cleanup - let the service manage its own lifecycle
    // The service has duplicate connection prevention, so multiple calls to connect() are safe
  }, [isAuthenticated, user]);

  // Register calendar service callbacks
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      return;
    }

    // Register callback for active bots list (sent on connect)
    calendarWebSocketService.onActiveBots((bots) => {
      // If we received active bots, the calendar service is connected
      setIsCalendarConnected(true);

      setCalendarBots(bots);

      // Check if there are any active/resumable bots
      const activeBots = bots.filter(b => b.status === 'active' && b.botId);

      if (activeBots.length > 0 && !selectedBotId) {
        // Show resume modal if there are active bots and no bot is currently selected
        console.log('📅 Found active bots, showing resume modal:', activeBots.length);
        setResumableBots(activeBots);
        setShowResumeModal(true);
      } else if (activeBots.length === 0) {
        // No active bots, close resume modal if it's open
        setShowResumeModal(false);
        setResumableBots([]);
      }
    });

    // Register callback for new bots going active
    calendarWebSocketService.onBotActive((bot) => {
      console.log('📅 New bot active:', bot);
      console.log('🔍 DEBUG: Bot isSalespersonHost:', bot.isSalespersonHost);
      // Add to list if not already there
      // Match by botId if both have it, otherwise match by botSessionId (handles scheduled → joining transition)
      setCalendarBots(prev => {
        const exists = prev.some(b =>
          (bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)
        );
        if (exists) {
          // Update existing bot (match by botId first, fallback to botSessionId)
          return prev.map(b =>
            ((bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)) ? bot : b
          );
        }
        // Add new bot
        return [...prev, bot];
      });

      // NOTE: Auto-selection removed - user must explicitly click to select a bot
      // This prevents issues with stale closures and multiple bots being selected

      // Dispatch custom event for MeetBotModal to know bot is truly active (not just in lobby)
      console.log('📅 Bot is now active, dispatching bot-active event:', bot.botId);
      window.dispatchEvent(new CustomEvent('bot-active', {
        detail: { botId: bot.botId, status: bot.status }
      }));

      // Auto-close modal when bot becomes active (backup in case modal event listener fails)
      setShowMeetBotModal(false);
    });

    // Register callback for bot status changes
    calendarWebSocketService.onBotStatusChange((bot) => {
      console.log('📅 Bot status changed:', bot);
      console.log('🔍 DEBUG: Bot isSalespersonHost:', bot.isSalespersonHost);

      // Update bot list
      // Match by botId if both have it, otherwise match by botSessionId (handles scheduled → joining transition)
      let updatedBots: CalendarBot[] = [];
      setCalendarBots(prev => {
        const exists = prev.some(b =>
          (bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)
        );
        if (exists) {
          updatedBots = prev.map(b =>
            ((bot.botId && b.botId && b.botId === bot.botId) || (b.botSessionId === bot.botSessionId)) ? bot : b
          );
        } else {
          // Bot not in list yet (e.g., just moved to 'joining'), add it
          updatedBots = [...prev, bot];
        }
        return updatedBots;
      });

      // NOTE: Auto-selection removed - user must explicitly click to select a bot
      // This prevents issues with stale closures and multiple bots being selected

      // If selected bot terminated, clear selection and bot connected state
      const isSelectedBot = bot.botId === selectedBotId ||
                           bot.botId === currentBotDisplayId;

      if (isSelectedBot && bot.status === 'terminated') {
        console.log('📅 Selected bot terminated, clearing selection and bot state');
        setSelectedBotId(null);
        setIsBotConnected(false);
        setCurrentBotType(null);
        setCurrentBotSessionId(null);
        setCurrentBotDisplayId(null);
      }


      // If bot just became active and is currently selected, ensure recall connection
      if (bot.botId === selectedBotId && bot.status === 'active' && bot.websocketUrl) {
        const currentRoomId = sessionManager.getRoomId();
        if (currentRoomId === bot.botId) {
          console.log('📅 Bot is now active, ensuring recall connection');
          // Reconnect to ensure we have recall WebSocket
          handleBotSelection(bot.botId, bot.websocketUrl, bot.botSessionId);
        }
      }
    });

    // No cleanup needed - websocketService manages calendar connection lifecycle
  }, [isAuthenticated, user]);

  // Handle bot selection from calendar
  const handleBotSelection = async (botId: string, websocketUrl?: string, botSessionId?: string, isSalespersonHostParam?: boolean) => {
    if (!botId) return;

    console.log('📅 Bot clicked:', botId);

    // Set loading state
    setConnectingBotId(botId);

    // IMPORTANT: First disconnect from OLD bot's WebSocket BEFORE doing anything else
    // This prevents any potential sync of cleared state to the old bot's session
    const { recallWebSocketService } = await import('../services/recallWebSocketService');
    recallWebSocketService.disconnect();
    console.log('🔌 Disconnected from previous bot WebSocket');

    // Now it's safe to clear Redux state (old bot's WebSocket is disconnected)
    dispatch(clearConversation());
    dispatch(clearLandmineAlerts());
    dispatch(clearCompetitorAlerts());

    // Load historical data for clicked bot
    const firebaseUid = user?.uid;
    const resumeUrl = `${config.server.backendUrl}/api/conversations/sessions/${botId}/resume?firebase_uid=${firebaseUid}`;
    console.log('📡 Loading historical data from:', resumeUrl);

    try {
      const response = await fetch(resumeUrl);
      if (response.ok) {
        const sessionData = await response.json();
        if (sessionData.data) {
          console.log('📊 Loaded historical data:', {
            transcripts: sessionData.data.transcripts?.length || 0,
            objections: sessionData.data.detectedObjections?.length || 0,
            faqs: sessionData.data.detectedFAQs?.length || 0,
          });

          // Restore transcripts
          sessionData.data.transcripts?.forEach((t: any) => {
            dispatch(addTranscript({ text: t.text, speaker: t.speaker, timestamp: t.timestamp }));
          });

          // Helper: restore a question entry including its feedback if present
          const restoreQuestion = (q: any, category: string) => {
            dispatch(addQuestion({ question: q.question, category, relevance_score: q.relevance_score || 1, timestamp: q.timestamp }));
            // Restore feedback by finding the just-added pending entry in the store
            if (q.feedback?.rating) {
              const addedEntry = store.getState().conversation.entries.find(
                (e: any) => e.question === q.question && e.status === 'pending'
              );
              if (addedEntry) {
                dispatch(updateEntryFeedback({ id: addedEntry.id, feedback: q.feedback }));
              }
            }
            if (q.answer && q.status === 'completed') {
              dispatch(addAnswer({ question: q.question, answer: q.answer, timestamp: q.timestamp }));
            }
          };

          // Restore objections
          sessionData.data.detectedObjections?.forEach((q: any) => restoreQuestion(q, 'Objection'));

          // Restore FAQs
          sessionData.data.detectedFAQs?.forEach((q: any) => restoreQuestion(q, 'FAQ'));

          // Restore inquiries
          sessionData.data.newInquiries?.forEach((q: any) => restoreQuestion(q, 'New Inquiry'));

          // Restore alerts
          sessionData.data.landmineDetections?.forEach((alert: any) => {
            dispatch(addLandmineAlert({ ...alert, landmine_type: alert.landmine_type || 'legal_risk', can_say: alert.can_say || [], cannot_say: alert.cannot_say || [] }));
          });
          sessionData.data.competitorDetections?.forEach((alert: any) => {
            dispatch(addCompetitorAlert({ ...alert, advantages: alert.advantages || [], weaknesses: alert.weaknesses || [], talking_points: alert.talking_points || '' }));
          });
          sessionData.data.speechRateAnalysis?.alerts?.forEach((alert: any) => {
            dispatch(addSpeechRateAlert(alert));
          });

          console.log('✅ Historical data restored');
        }
      } else {
        console.log('ℹ️ No historical data (new session)');
      }
    } catch (err) {
      console.warn('⚠️ Could not load historical data:', err);
    }

    setSelectedBotId(botId);

    try {
      // Set reconnecting flag to prevent redirect
      dispatch(setReconnecting(true));
      console.log('🔄 Set isReconnecting flag for bot selection');

      // Import services
      const { sessionManager } = await import('../services/sessionManager');
      const { websocketService } = await import('../services/websocketService');
      const { recallWebSocketService } = await import('../services/recallWebSocketService');

      // Update sessionManager with the botId (room_id)
      sessionManager.setRoomId(botId);
      console.log(`✅ Updated sessionManager room_id to: ${botId}`);

      // If botSessionId not provided, try to find it from calendarBots
      if (!botSessionId) {
        const selectedBot = calendarBots.find(bot => bot.botId === botId);
        botSessionId = selectedBot?.botSessionId || undefined;

        // Also get websocketUrl if not provided
        if (!websocketUrl) {
          websocketUrl = selectedBot?.websocketUrl;
        }
      } else if (!websocketUrl) {
        // If botSessionId was provided but not websocketUrl, try to find websocketUrl
        const selectedBot = calendarBots.find(bot => bot.botId === botId);
        websocketUrl = selectedBot?.websocketUrl;
      }

      // Extract isSalespersonHost from parameter (if provided) or bot data
      let isSalespersonHostValue: boolean;

      if (isSalespersonHostParam !== undefined) {
        // Use the parameter value if explicitly provided
        isSalespersonHostValue = isSalespersonHostParam;
        console.log('🔍 DEBUG: Using isSalespersonHost from parameter:', isSalespersonHostValue);
      } else {
        // Fall back to finding it in the calendarBots array
        const selectedBot = calendarBots.find(bot => bot.botId === botId);
        isSalespersonHostValue = selectedBot?.isSalespersonHost ?? true; // Default to true

        // DEBUG: Log the bot data to verify isSalespersonHost
        console.log('🔍 DEBUG: Calendar bots array:', calendarBots);
        console.log('🔍 DEBUG: Selected bot:', selectedBot);
        console.log('🔍 DEBUG: isSalespersonHost from bot:', selectedBot?.isSalespersonHost);
        console.log('🔍 DEBUG: isSalespersonHostValue (final):', isSalespersonHostValue);
      }

      // Set in Redux BEFORE connecting services
      dispatch(setIsSalespersonHost(isSalespersonHostValue));
      console.log('💾 Set isSalespersonHost for calendar bot:', isSalespersonHostValue);

      // Verify Redux state was updated
      setTimeout(() => {
        const state = store.getState();
        console.log('🔍 DEBUG: Redux isSalespersonHost after dispatch:', state.conversation.isSalespersonHost);
      }, 0);

      console.log('📍 Bot selection details:', { botId, botSessionId, websocketUrl, isSalespersonHost: isSalespersonHostValue });

      // CRITICAL: Connect to Recall WebSocket service FIRST before other services
      if (websocketUrl) {
        console.log(`🔌 Connecting to Recall WebSocket service at: ${websocketUrl} for bot: ${botId}`);
        try {
          await recallWebSocketService.connect(botId, websocketUrl);
          console.log(`✅ Successfully connected to Recall WebSocket service for bot: ${botId}`);

          // Update clientId for calendar bot so it matches frontend
          try {
            const { generateClientId } = await import('../utils/clientId');
            const clientId = generateClientId();
            console.log('📱 Generated clientId for calendar bot:', clientId);

            const backendUrl = config.server.backendUrl || 'http://localhost:5000';
            const updateResponse = await fetch(`${backendUrl}/api/bots/${botId}/client`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ clientId }),
            });

            if (updateResponse.ok) {
              console.log('✅ Updated clientId for calendar bot');
            } else {
              console.warn('⚠️ Failed to update clientId for calendar bot');
            }
          } catch (clientIdError) {
            console.warn('⚠️ Error updating clientId:', clientIdError);
            // Continue - this is not critical
          }
        } catch (recallError) {
          console.error(`❌ Failed to connect to Recall WebSocket:`, recallError);
          // Continue anyway - other services might still work
        }
      } else {
        console.warn('⚠️ No websocketUrl available for calendar bot, skipping Recall connection');
      }

      // Reconnect all WebSocket services with the new room_id
      console.log(`🔄 Reconnecting all services with new room_id: ${botId}`);
      await websocketService.reconnectWithNewRoomId();

      // Reconnect landmine and competitor services
      landmineWebSocketService.joinRoom(botId);
      competitorWebSocketService.joinRoom(botId);

      console.log(`✅ All services connected with bot_id "${botId}" as room_id`);

      // Mark as calendar bot for removal
      setCurrentBotType('calendar');
      setCurrentBotSessionId(botSessionId || null); // UUID for API calls
      setCurrentBotDisplayId(botId); // Recall.ai bot ID for display
      setIsBotConnected(true);

      // Note: Bot status is managed by calendar_sync_service - no manual update needed
      console.log('✅ Set bot state:', {
        currentBotType: 'calendar',
        currentBotSessionId: botSessionId,
        currentBotDisplayId: botId,
        isBotConnected: true
      });

      // Clear reconnecting flag and loading state
      dispatch(setReconnecting(false));
      setConnectingBotId(null);
      console.log('✅ Cleared isReconnecting flag and loading state');
    } catch (error) {
      console.error('❌ Error switching to bot:', error);
      dispatch(setReconnecting(false));
      setConnectingBotId(null); // Clear loading state on error
      alert('Failed to connect to bot. Please try again.');
    }
  };

  const handleResumeSession = async (bot: CalendarBot) => {
    console.log('🔄 Resuming session for bot:', bot.botId);

    try {
      // Close the resume modal
      setShowResumeModal(false);

      // Fetch historical data from backend
      const firebaseUid = user?.uid;
      const resumeUrl = `${config.server.backendUrl}/api/conversations/sessions/${bot.botId}/resume?firebase_uid=${firebaseUid}`;
      console.log('📡 Fetching session data from:', resumeUrl);

      const response = await fetch(resumeUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch session data: ${response.statusText}`);
      }

      const sessionData = await response.json();
      console.log('✅ Received session data:', sessionData);

      // Restore Redux state with historical data
      if (sessionData.data) {
        console.log('📊 Restoring historical data:', {
          transcripts: sessionData.data.transcripts?.length || 0,
          objections: sessionData.data.detectedObjections?.length || 0,
          faqs: sessionData.data.detectedFAQs?.length || 0,
          inquiries: sessionData.data.newInquiries?.length || 0,
          landmineAlerts: sessionData.data.landmineDetections?.length || 0,
          competitorAlerts: sessionData.data.competitorDetections?.length || 0,
          speechRateAlerts: sessionData.data.speechRateAnalysis?.alerts?.length || 0
        });

        // 1. Restore transcripts
        sessionData.data.transcripts?.forEach((t: any) => {
          dispatch(addTranscript({
            text: t.text,
            speaker: t.speaker,
            timestamp: t.timestamp
          }));
        });

        // 2. Restore objections (category: 'Objection')
        sessionData.data.detectedObjections?.forEach((q: any) => {
          dispatch(addQuestion({
            question: q.question,
            category: 'Objection',
            relevance_score: q.relevance_score || 1,
            timestamp: q.timestamp
          }));
          // If answered, add answer too
          if (q.answer && q.status === 'completed') {
            dispatch(addAnswer({
              question: q.question,
              answer: q.answer,
              timestamp: q.timestamp
            }));
          }
        });

        // 3. Restore FAQs (category: 'FAQ')
        sessionData.data.detectedFAQs?.forEach((q: any) => {
          dispatch(addQuestion({
            question: q.question,
            category: 'FAQ',
            relevance_score: q.relevance_score || 1,
            timestamp: q.timestamp
          }));
          if (q.answer && q.status === 'completed') {
            dispatch(addAnswer({
              question: q.question,
              answer: q.answer,
              timestamp: q.timestamp
            }));
          }
        });

        // 4. Restore new inquiries (category: 'New Inquiry')
        sessionData.data.newInquiries?.forEach((q: any) => {
          dispatch(addQuestion({
            question: q.question,
            category: 'New Inquiry',
            relevance_score: q.relevance_score || 1,
            timestamp: q.timestamp
          }));
          if (q.answer && q.status === 'completed') {
            dispatch(addAnswer({
              question: q.question,
              answer: q.answer,
              timestamp: q.timestamp
            }));
          }
        });

        // 5. Restore landmine alerts
        sessionData.data.landmineDetections?.forEach((alert: any) => {
          dispatch(addLandmineAlert({
            ...alert,
            can_say: alert.can_say || [],
            cannot_say: alert.cannot_say || []
          }));
        });

        // 8. Restore competitor alerts
        sessionData.data.competitorDetections?.forEach((alert: any) => {
          dispatch(addCompetitorAlert({
            ...alert,
            advantages: alert.advantages || [],
            weaknesses: alert.weaknesses || [],
            talking_points: alert.talking_points || ''
          }));
        });

        // 9. Restore speech rate alerts
        sessionData.data.speechRateAnalysis?.alerts?.forEach((alert: any) => {
          dispatch(addSpeechRateAlert(alert));
        });

        // 10. Restore sales playbook state
        if (sessionData.data.talkTrackUsage) {
          const usage = sessionData.data.talkTrackUsage;

          // First fetch industries to populate the dropdown
          const industriesResult = await dispatch(fetchIndustries() as any);
          const industries = industriesResult?.payload || [];

          // Find the matching industry to get available roles
          const matchingIndustry = industries.find(
            (ind: any) => ind.industry === usage.selectedIndustry
          );
          const availableRoles = matchingIndustry?.roles || [];

          // Build tracking cache from questions/notes/pitches/advanced (grouped by role within the current industry)
          interface TrackingCacheEntry {
            askedQuestions: string[];
            questionNotes: Record<string, { note: string; timestamp: string }>;
            checkedPitches: string[];
            pitchNotes: Record<string, { note: string; timestamp: string }>;
            checkedAdvanced: string[];
            advancedNotes: Record<string, { note: string; timestamp: string }>;
          }
          const trackingCache: Record<string, TrackingCacheEntry> = {};

          const createEmptyEntry = (): TrackingCacheEntry => ({
            askedQuestions: [],
            questionNotes: {},
            checkedPitches: [],
            pitchNotes: {},
            checkedAdvanced: [],
            advancedNotes: {}
          });

          // Determine default role (use saved role, or first available, or 'general')
          const defaultRole = usage.selectedRole || availableRoles[0]?.name || 'general';
          const defaultRoleDisplayName = usage.selectedRoleDisplayName ||
            availableRoles.find((r: any) => r.name === defaultRole)?.displayName || 'General';

          // Group questions by role (all should be from current industry)
          usage.questionsAsked?.forEach((q: any) => {
            const role = q.role || defaultRole;
            const key = `${usage.selectedIndustry}|${role}`;

            if (!trackingCache[key]) {
              trackingCache[key] = createEmptyEntry();
            }
            if (!trackingCache[key].askedQuestions.includes(q.questionId)) {
              trackingCache[key].askedQuestions.push(q.questionId);
            }
          });

          // Group question notes by role (all should be from current industry)
          usage.questionNotes?.forEach((n: any) => {
            const role = n.role || defaultRole;
            const key = `${usage.selectedIndustry}|${role}`;

            if (!trackingCache[key]) {
              trackingCache[key] = createEmptyEntry();
            }
            trackingCache[key].questionNotes[n.questionId] = {
              note: n.note,
              timestamp: n.timestamp
            };
          });

          // Group pitches by role
          usage.pitchesUsed?.forEach((p: any) => {
            const role = p.role || defaultRole;
            const key = `${usage.selectedIndustry}|${role}`;

            if (!trackingCache[key]) {
              trackingCache[key] = createEmptyEntry();
            }
            if (!trackingCache[key].checkedPitches.includes(p.pitchId)) {
              trackingCache[key].checkedPitches.push(p.pitchId);
            }
          });

          // Group pitch notes by role
          usage.pitchNotes?.forEach((n: any) => {
            const role = n.role || defaultRole;
            const key = `${usage.selectedIndustry}|${role}`;

            if (!trackingCache[key]) {
              trackingCache[key] = createEmptyEntry();
            }
            trackingCache[key].pitchNotes[n.pitchId] = {
              note: n.note,
              timestamp: n.timestamp
            };
          });

          // Group advanced by role
          usage.advancedUsed?.forEach((a: any) => {
            const role = a.role || defaultRole;
            const key = `${usage.selectedIndustry}|${role}`;

            if (!trackingCache[key]) {
              trackingCache[key] = createEmptyEntry();
            }
            if (!trackingCache[key].checkedAdvanced.includes(a.advancedId)) {
              trackingCache[key].checkedAdvanced.push(a.advancedId);
            }
          });

          // Group advanced notes by role
          usage.advancedNotes?.forEach((n: any) => {
            const role = n.role || defaultRole;
            const key = `${usage.selectedIndustry}|${role}`;

            if (!trackingCache[key]) {
              trackingCache[key] = createEmptyEntry();
            }
            trackingCache[key].advancedNotes[n.advancedId] = {
              note: n.note,
              timestamp: n.timestamp
            };
          });

          // Get current selection's data from cache
          const currentKey = `${usage.selectedIndustry}|${defaultRole}`;
          const currentData = trackingCache[currentKey] || createEmptyEntry();

          // Restore the selection state with tracking cache (only for current industry's roles)
          dispatch(restoreSalesPlaybookState({
            selectedIndustry: usage.selectedIndustry,
            selectedIndustryDisplayName: usage.selectedIndustryDisplayName,
            selectedRole: defaultRole,
            selectedRoleDisplayName: defaultRoleDisplayName,
            availableRoles: availableRoles,
            askedQuestions: currentData.askedQuestions,
            questionNotes: currentData.questionNotes,
            checkedPitches: currentData.checkedPitches,
            pitchNotes: currentData.pitchNotes,
            checkedAdvanced: currentData.checkedAdvanced,
            advancedNotes: currentData.advancedNotes,
            trackingCache: trackingCache
          }));

          // Fetch the role's questions to populate the panel
          if (usage.selectedIndustry) {
            dispatch(fetchRoleData({
              industry: usage.selectedIndustry,
              roleName: defaultRole
            }) as any);
          }

          console.log('📋 Sales playbook state restored:', {
            industry: usage.selectedIndustry,
            role: usage.selectedRole,
            availableRoles: availableRoles.length,
            questionsAsked: usage.questionsAsked?.length || 0,
            notes: usage.questionNotes?.length || 0
          });
        }

        console.log('✅ Historical data restored to Redux store');
      }

      // Now connect to the bot using existing handleBotSelection logic
      await handleBotSelection(
        bot.botId,
        bot.websocketUrl,
        bot.botSessionId,
        bot.isSalespersonHost
      );

      console.log('✅ Session resumed successfully');
    } catch (error) {
      console.error('❌ Error resuming session:', error);
      alert('Failed to resume session. Please try again.');
      setShowResumeModal(false);
    }
  };

  const handleDismissResumeModal = () => {
    console.log('🚫 User dismissed resume modal, starting fresh session');
    setShowResumeModal(false);
    setResumableBots([]);
  };

  // Track the true height of the fixed header so the main content margin-top stays exact
  useEffect(() => {
    const el = fixedHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setFixedHeaderHeight(el.offsetHeight));
    observer.observe(el);
    setFixedHeaderHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, []);

  // Dynamically compute the max height for SalesPlaybookPanel so it fills all available
  // left-column space after the Audio Settings and BotSelector sections.
  // Runs on mount and whenever the left column is resized (browser resize / panel toggle).
  useEffect(() => {
    const col = leftColRef.current;
    if (!col) return;

    const COL_GAPS = 16; // space-y-2 (8px) × 2 gaps between 3 children (audio, bot, playbook)

    const updateMax = () => {
      const audioH = audioSettingsRef.current?.clientHeight ?? 0;
      const botH = botSelectorRef.current?.clientHeight ?? 0;
      const colH = col.clientHeight;
      const newMax = Math.max(150, colH - audioH - botH - COL_GAPS);

      setMaxPanelHeight(newMax);

      if (!isPanelInitialized.current) {
        // Pin to max on first render so panel starts fully expanded
        setSalesPlaybookPanelHeight(newMax);
        isPanelInitialized.current = true;
      } else {
        // If the column shrinks (e.g. browser resize), clamp current height to new max
        setSalesPlaybookPanelHeight(prev => Math.min(prev, newMax));
      }
    };

    const observer = new ResizeObserver(updateMax);
    observer.observe(col);
    updateMax();

    return () => observer.disconnect();
  }, []);

  // Start speech rate monitoring when connected AND audio capture is active AND authenticated
  useEffect(() => {
    if (isConnected && speechRateSettings.enabled && isAudioCaptureActive && isAuthenticated && tokenInfo?.token) {
      // console.log('🎤 Starting speech rate monitoring...');
      
      speechRateService.startMonitoring(
        () => transcripts, // Get transcripts function
        () => ({ 
          time_window: speechRateSettings.time_window, 
          max_words: speechRateSettings.max_words 
        }), // Get settings function
        (analysis) => {
          dispatch(updateSpeechRateAnalysis(analysis));
        },
        2000 // Check every 2 seconds
      );

      return () => {
        // console.log('🎤 Stopping speech rate monitoring...');
        speechRateService.stopMonitoring();
      };
    } else if (!isAudioCaptureActive) {
      // Stop monitoring if audio capture is not active
      speechRateService.stopMonitoring();
    }
  }, [isConnected, speechRateSettings.enabled, speechRateSettings.time_window, speechRateSettings.max_words, transcripts, dispatch, isAudioCaptureActive, isAuthenticated, tokenInfo]);

  // Test speech rate service on component mount (only if authenticated)
  useEffect(() => {
    if (isAuthenticated && tokenInfo?.token) {
      testSpeechRateService();
    }
  }, [isAuthenticated, tokenInfo]);

  // Register cleanup handlers when component mounts (only if authenticated and connected)
  useEffect(() => {
    if (isConnected && isAuthenticated && tokenInfo?.token) {
      cleanupService.registerCleanupHandlers();
      // Start a new conversation session
      const sessionInfo = websocketService.getSessionInfo();
      conversationService.startSession(sessionInfo?.roomId, sessionInfo?.clientId);
    }
    
    return () => {
      // Cleanup when component unmounts
      cleanupService.unregisterCleanupHandlers();
    };
  }, [isConnected, isAuthenticated, tokenInfo]);

  // No redirect based on connection status - users can view the page and connect when ready
  // Authentication is still checked in the initial useEffect above

  // Handle speech rate alerts
  useEffect(() => {
    if (lastSpeechRateAlert) {
      setShowSpeechAlert(true);
      // Track the alert for session saving
      setSpeechRateAlerts(prev => [...prev, lastSpeechRateAlert]);

      // Auto-hide alert after 7 seconds
      const timer = setTimeout(() => {
        setShowSpeechAlert(false);
        dispatch(clearSpeechRateAlert());
      }, 7000);

      return () => clearTimeout(timer);
    }
  }, [lastSpeechRateAlert, dispatch]);

  const handleSignOut = async () => {
    try {
      // Perform manual cleanup before signing out
      cleanupService.manualCleanup();

      // Clear alerts before signing out
      competitorWebSocketService.clearAlerts();
      landmineWebSocketService.clearAlerts();

      // Reset Redux state
      dispatch(resetConnection());
      dispatch(clearConversation());

      // Sign out from Firebase
      const auth = getAuth();
      await signOut(auth);

      // Dispatch logout action to clear auth state
      dispatch(logoutUser() as any);

      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
      // Still navigate to login even if sign out fails
      navigate('/login');
    }
  };

  const handleClearFeed = () => {
    if (window.confirm('Are you sure you want to clear the conversation feed?')) {
      dispatch(clearConversation());
    }
  };

  const dismissSpeechAlert = () => {
    setShowSpeechAlert(false);
    dispatch(clearSpeechRateAlert());
  };

  const handleCaptureStateChange = (isCapturing: boolean) => {
    setIsAudioCaptureActive(isCapturing);
    // console.log(`🎤 Audio capture state changed: ${isCapturing ? 'ACTIVE' : 'STOPPED'}`);
  };

  const handleTranscription = (result: TranscriptionResult) => {
    // Only process transcriptions if authenticated
    if (!isAuthenticated || !tokenInfo?.token) {
      console.warn('🔐 Cannot process transcription - user not authenticated');
      return;
    }

    // Add transcript to Redux store for display in LiveTranscriptBar (Redux store handles deduplication)
    dispatch(addTranscript({
      text: result.transcript,
      speaker: result.speaker || 'salesperson', // Use detected speaker or default to salesperson
      timestamp: result.timestamp.toISOString()
    }));

    // ============================================================================
    // LEGACY MANUAL MODE CODE (DEPRECATED)
    // ============================================================================
    // This code path is for manual microphone input via audioCaptureService/deepgram_proxy
    // The PRIMARY workflow now uses Recall.ai bots where:
    //   - recall_websocket_service.py (port 3456) receives transcripts from Recall.ai
    //   - recall_websocket_service.py broadcasts to ALL backend services automatically
    //   - Frontend ONLY receives results, does NOT send transcripts
    //
    // This manual mode code is kept for backward compatibility but should be
    // considered deprecated in favor of the bot workflow.
    // ============================================================================

    // Send ALL final transcripts to landmine detection service & competition detection service
    if (result.isFinal) {
      const speaker = result.speaker || 'salesperson';
      const timestamp = result.timestamp.toISOString();
      landmineWebSocketService.sendTranscript(result.transcript, speaker, timestamp);
      competitorWebSocketService.sendTranscript(result.transcript, 'customer', timestamp);
    }

    // Route transcripts to appropriate AI services based on speaker
    if (result.isFinal) {
      if (result.speaker === 'customer' && result.audioSource === 'speaker') {
        // console.log('🤖 Routing customer transcript to AI pipeline for objection detection');
        // Send customer transcript to AI server via WebSocket for objection detection and response generation
        websocketService.sendMessage({
          type: 'transcript',
          data: {
            text: result.transcript,
            speaker: 'customer',
            timestamp: result.timestamp.toISOString(),
            source: 'speaker_audio'
          }
        });

      } else if (result.speaker === 'salesperson' && result.audioSource === 'microphone') {
        // Salesperson audio is already being processed by speech rate service
        // No additional WebSocket message needed as speech rate service monitors the Redux store
      }
    }
  };

  // Don't render anything if not authenticated or not verified
  if (!isAuthenticated || !tokenInfo?.token || !isMongoVerified) {
    return null; // Will redirect via useEffect
  }

  // No connection check needed - users can view the page and connect when ready

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Fixed Top Bar — includes navbar, speech alert, and info bar */}
      <div ref={fixedHeaderRef} className="fixed top-0 left-0 right-0 z-50">
        <TopBar
          onSignOut={handleSignOut}
          clientId={clientId}
          roomId={selectedBotId || standaloneSession?.roomId || null}
        />

        {/* Speech Rate Alert Banner */}
        {showSpeechAlert && lastSpeechRateAlert && (
          <div className="bg-red-500 text-white px-6 py-3 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-xl">⚠️</span>
              <div>
                <span className="font-bold">Speaking too fast: </span>
                <span>
                  {lastSpeechRateAlert.total_words} words / {lastSpeechRateAlert.time_window} secs. Maximum allowed is {lastSpeechRateAlert.max_words} words
                </span>
              </div>
            </div>
            <button
              onClick={dismissSpeechAlert}
              className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded text-sm transition-colors"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Combined info bar — live transcript */}
        <CombinedInfoBar />
      </div>

      {/* Landmine Alerts */}
      <LandmineAlertsContainer
        alerts={landmineAlerts}
        onDismiss={(index) => dismissLandmineAlert(index)}
      />

      {/* Competitor Alerts */}
      <div className="fixed top-32 right-4 z-40 space-y-2">
        {competitorAlerts.map((alert: any, index: number) => (
          <CompetitorAlert
            key={`competitor-alert-${index}`}
            alert={alert}
            onDismiss={() => dismissCompetitorAlert(index)}
          />
        ))}
      </div>

      {/* Main Content Area with proper top margin */}
      <div
        className="flex-1 flex min-h-0 gap-4 px-4"
        style={{
          marginTop: `${fixedHeaderHeight}px`,
          marginBottom: '40px'
        }}
      >
        {/* Left side - Audio Settings & Live Transcripts (40%) */}
        <div ref={leftColRef} className="w-2/5 flex flex-col min-h-0 space-y-2">
          {/* Audio Settings Button */}
          <div ref={audioSettingsRef} className="bg-white rounded-lg shadow-md p-2">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-gray-900">Audio Settings</h3>
              <div className="flex space-x-2">
                {/* Hide Connect Bot button when standalone session is active */}
                {!isStandaloneSessionConnected && (
                  <button
                    onClick={() => {
                      console.log('🚀 Opening MeetBotModal with state:', {
                        isBotConnected,
                        currentBotType,
                        currentBotSessionId,
                        currentBotDisplayId,
                        selectedBotId
                      });
                      setShowMeetBotModal(true);
                    }}
                    className={`${isBotConnected ? 'bg-red-500 hover:bg-red-600' : 'bg-purple-500 hover:bg-purple-600'} text-white px-3 py-1 text-sm rounded-md transition-colors flex items-center space-x-1`}
                  >
                    <span>🤖</span>
                    <span>{isBotConnected ? 'Remove Bot' : 'Connect Bot'}</span>
                  </button>
                )}
                {/* Manual Audio Capture button - disabled when any bot or standalone session is active */}
                {/* <button
                  onClick={() => setShowManualCaptureModal(true)}
                  disabled={isBotConnected || isStandaloneSessionConnected || calendarBots.some(bot =>
                    bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
                  )}
                  className={`${
                    isBotConnected || isStandaloneSessionConnected || calendarBots.some(bot =>
                      bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
                    )
                      ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  } px-4 py-2 rounded-md transition-colors flex items-center space-x-2`}
                  title={
                    isStandaloneSessionConnected
                      ? 'Desktop app session is active - stop it first to use manual capture'
                      : isBotConnected || calendarBots.some(bot =>
                          bot.status === 'active' || bot.status === 'joining' || bot.status === 'creating'
                        )
                        ? 'Disable bot connection first to use manual capture'
                        : 'Capture audio directly from your computer'
                  }
                >
                  <span>🎤</span>
                  <span>Manual Capture</span>
                </button> */}
              </div>
            </div>
            
            {/* Status indicators */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                {/* Bot/Audio capture status */}
                <div className="flex items-center space-x-1">
                  <div className={`w-1.5 h-1.5 rounded-full ${isAudioCaptureActive ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                  <span className="text-xs text-gray-600">
                    {isAudioCaptureActive ? 'Live monitoring active' : 'Monitoring inactive'}
                  </span>
                </div>
                {/* Standalone session status */}
                {isStandaloneLobbyConnected && (
                  <div className="flex items-center space-x-1">
                    <div className={`w-1.5 h-1.5 rounded-full ${isStandaloneSessionConnected ? 'bg-purple-500 animate-pulse' : 'bg-purple-300'}`} />
                    <span className="text-xs text-gray-600">
                      {standaloneSession ? (
                        <>
                          <span className="text-purple-600 font-medium">Desktop:</span> {standaloneSession.sessionName}
                        </>
                      ) : (
                        'Desktop app lobby connected'
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Bot Selector - Calendar Integration */}
          <div ref={botSelectorRef}>
          <BotSelector
            bots={calendarBots}
            selectedBotId={selectedBotId}
            connectingBotId={connectingBotId}
            onSelectBot={handleBotSelection}
            isCalendarConnected={isCalendarConnected}
            onBotTerminated={async () => {
              console.log('🔴 BotSelector reported bot terminated, clearing state immediately');

              // Use the same termination flow as MeetBotModal for consistency
              const sessionIdToRemove = currentBotDisplayId || currentBotSessionId;

              if (!sessionIdToRemove) {
                console.error('❌ No session ID to remove! Cannot proceed.');
                return;
              }

              // Get session name before clearing state
              const currentBot = calendarBots.find(b =>
                b.botId === currentBotDisplayId || b.botSessionId === currentBotSessionId
              );
              const sessionName = currentBot?.eventTitle || 'Session';

              try {
                console.log('🛑 Removing bot via unified termination flow:', sessionIdToRemove);

                const backendUrl = config.server.backendUrl || 'http://localhost:5000';

                // Call backend terminate endpoint (works for both manual and calendar bots)
                const response = await fetch(`${backendUrl}/api/bots/${sessionIdToRemove}/terminate`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                });

                if (!response.ok) {
                  console.warn('⚠️ Backend terminate failed:', response.status);
                  // Continue with cleanup even if terminate fails
                }

                console.log('✅ Bot terminated successfully');

                // Set call end time before disconnecting
                const { sessionManager } = await import('../services/sessionManager');
                sessionManager.setCallEndTime();

                // Disconnect from ALL WebSocket services
                console.log('🔌 Disconnecting from all WebSocket services...');

                const { recallWebSocketService } = await import('../services/recallWebSocketService');
                const { websocketService } = await import('../services/websocketService');
                const { landmineWebSocketService } = await import('../services/landmineWebSocketService');
                const { competitorWebSocketService } = await import('../services/competitorWebSocketService');

                recallWebSocketService.disconnect();
                websocketService.disconnect();
                landmineWebSocketService.disconnect();
                competitorWebSocketService.disconnect();

                console.log('✅ All WebSocket services disconnected');

                // Show session summary modal BEFORE clearing state (so stats are computed correctly)
                computeAndShowSummary(sessionName);

                // Clear bot connection state
                setIsBotConnected(false);
                setCurrentBotType(null);
                setCurrentBotSessionId(null);
                setCurrentBotDisplayId(null);
                setSelectedBotId(null);
                setConnectingBotId(null);

                // Session data is now automatically saved by saver_service via calendar_sync polling
                console.log('💾 Session automatically saved by saver_service');
              } catch (error) {
                console.error('❌ Error during bot termination:', error);
                // Still clear local state even if termination fails
                setIsBotConnected(false);
                setCurrentBotType(null);
                setCurrentBotSessionId(null);
                setCurrentBotDisplayId(null);
                setSelectedBotId(null);
                setConnectingBotId(null);
              }
            }}
          />
          </div>

          {/* Sales Playbook Panel — fills all remaining left-column space */}
          <SalesPlaybookPanel
            height={salesPlaybookPanelHeight}
            onHeightChange={setSalesPlaybookPanelHeight}
            minHeight={150}
            maxHeight={maxPanelHeight}
            isSessionActive={isBotConnected}
          />
        </div>

        {/* Right side - AI Conversation Analysis (60%) */}
        <div className="w-3/5 flex flex-col min-h-0">
          <ConversationFeed />
        </div>
      </div>

      {/* Fixed Status Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-2 z-40">
        <div className="text-center text-xs text-gray-500">
          Sales Guru - AI-Powered Sales Assistant | Real-time conversation analysis active
        </div>
      </div>
      {/* Meeting Bot Modal */}
      <MeetBotModal
        isOpen={showMeetBotModal}
        isBotConnected={isBotConnected}
        botType={currentBotType}
        botSessionId={currentBotSessionId}
        botDisplayId={currentBotDisplayId}
        setCalendarBots={setCalendarBots}
        defaultBotName={calendarSettings?.botName}
        onBotConnectionStateChange={(isConnected) => {
          console.log(`🔄 Bot connection state changed to: ${isConnected ? 'connected' : 'disconnected'}`);
          setIsBotConnected(isConnected);

          // Note: Bot status is managed by calendar_sync_service - no manual update needed

          if (!isConnected) {
            // Clear bot type and session info when disconnected
            setCurrentBotType(null);
            setCurrentBotSessionId(null);
            setCurrentBotDisplayId(null);
            setSelectedBotId(null);
            setConnectingBotId(null);
          }
        }}
        onClose={() => {
          console.log('🔒 Modal closed by user or after successful connection');
          setShowMeetBotModal(false);
        }}
        onBotCreated={async (botId, websocketUrl) => {
          console.log(`🤖 Bot created with bot_id: ${botId}`);
          console.log(`🔌 WebSocket URL received: ${websocketUrl}`);

          try {
            // Set reconnecting flag to prevent redirect during reconnection
            dispatch(setReconnecting(true));
            console.log('🔄 Set isReconnecting flag to prevent redirect');

            // Import services
            const { sessionManager } = await import('../services/sessionManager');
            const { websocketService } = await import('../services/websocketService');
            const { recallWebSocketService } = await import('../services/recallWebSocketService');

            // Update sessionManager with the bot_id
            sessionManager.setRoomId(botId);
            console.log(`✅ Updated sessionManager room_id to: ${botId}`);

            // Connect to Recall WebSocket service
            console.log(`🔌 Connecting to Recall WebSocket service at: ${websocketUrl}`);
            await recallWebSocketService.connect(botId, websocketUrl);

            // Reconnect all other WebSocket services with the new room_id
            console.log(`🔄 Reconnecting all services with new room_id: ${botId}`);
            await websocketService.reconnectWithNewRoomId();

            console.log(`✅ All services connected with bot_id "${botId}" as room_id`);

            // Mark as manual bot for removal
            setCurrentBotType('manual');
            setCurrentBotSessionId(botId); // For manual bots, botId is the session ID
            setCurrentBotDisplayId(botId); // Same for display

            // Auto-select the manual bot so it shows data immediately
            setSelectedBotId(botId);
            console.log(`✅ Auto-selected manual bot: ${botId}`);

            // DON'T mark bot as connected here - wait for the modal to notify us
            // via onBotConnectionStateChange callback after the bot actually joins
            // setIsBotConnected(true);

            // Clear reconnecting flag
            dispatch(setReconnecting(false));
            console.log('✅ Cleared isReconnecting flag');

            // DON'T close the modal here - let the MeetBotModal handle its own closing
            // after the bot actually joins the meeting
            // setShowMeetBotModal(false);
          } catch (error) {
            console.error('❌ Error reconnecting services:', error);

            // Clear reconnecting flag on error
            dispatch(setReconnecting(false));

            alert('Failed to connect services with bot. Please try disconnecting and reconnecting.');
            // Don't close modal on error - let user see the error message in the modal
            // setShowMeetBotModal(false);
          }
        }}
      />

      {/* Resume Session Modal */}
      <ResumeSessionModal
        isOpen={showResumeModal}
        bots={resumableBots}
        onResume={handleResumeSession}
        onDismiss={handleDismissResumeModal}
      />

      {/* Manual Audio Capture Modal */}
      <ManualCaptureModal
        isOpen={showManualCaptureModal}
        onClose={() => setShowManualCaptureModal(false)}
      />

      {/* Session Summary Modal - shown when bot is stopped */}
      {sessionSummaryData && (
        <SessionSummaryModal
          isOpen={showSessionSummary}
          onClose={closeSummary}
          onClearSession={clearSessionData}
          summary={sessionSummaryData}
        />
      )}
    </div>
  );
};

export default ConversationPage;